import { LockOutlined } from '@ant-design/icons';
import { Descriptions, Table, Tag, Typography } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React from 'react';
import NumberFormat from 'react-number-format';
import { Link } from 'react-router-dom';
import { NOTE_TYPES } from './fields';
import { NoteAltoStatus } from './NoteAltoStatus';
import {
  NoteWalletBalanceCell,
  useNoteWalletBalances,
} from './NoteWalletBalanceCell';

// What the SYSTEM writes about a note (the create, the funding rails, Alto, Equity Trust, the note-cycle
// scheduler): the product and the listing code it was born with, whether money has moved (what locks
// the amounts, yield and term in the form), the funded figures, the cycle and the yield trail. No
// inputs — nothing here reaches `adminUpdateInvestment`.
//
// OUT OF THE EDIT FLOW (TL, 2026-09-12): this used to be a Collapse at the BOTTOM of the same column as
// the form, so the operator scrolled the whole fact sheet past a panel nobody edits. It is now the
// "System" TAB beside "Edit" (PropertyDetails) — a different NATURE of thing, not one more section of
// the form — so it needs no fold of its own and nothing is intercalated between the steps and the Save.
//
// ALTO LIVES HERE (TL, 2026-09-12): a note is registered as an offering when it is CREATED, so what Alto
// holds is state, not an operator action — it reads beside the listing that names it (NoteAltoStatus),
// with a "Register in Alto" button only while the registration has something left to do (the retry for a
// note whose birth failed). The header shows the note's own flag; the LIVE check against Alto is this one.

const ALTO_LOCKED = 'Registered in Alto — the listing is the offering id';
const LISTING_WHY = 'Generated from the address when the note was created. It never changes.';

const { Text } = Typography;

const money = (n) => (
  <NumberFormat
    displayType="text"
    value={n || 0}
    thousandSeparator={true}
    prefix={'$'}
    renderText={(value) => <span>{value}</span>}
  />
);

const date = (d) => (d ? moment(d).format('LLL') : '—');

const cycleLink = (id) => (id ? <Link to={`/properties-and-investments/${id}`}>{id}</Link> : '—');

const yieldHistoryColumns = [
  { title: 'Net yield', dataIndex: 'netYield', render: (v) => `${v}%` },
  { title: 'Changed at', dataIndex: 'changedAt', render: date },
  { title: 'Changed by', dataIndex: 'changedBy', render: (v) => v || '—' },
];

export function NoteSystemPanel({ investment, refetchNote }) {
  const isConstitution = investment?.noteType === NOTE_TYPES.constitution;
  const isCreditFund = investment?.noteType === NOTE_TYPES.creditFund;
  const yieldHistory = investment?.yieldHistory || [];
  // Two numbers, two names (TL, 2026-10-30): "Balance" is what sits in the note's Sila wallet right now
  // (~0 once the house swept it to a bank to lend), "Raised" is what investors put in, from the ledger.
  const balances = useNoteWalletBalances(investment?._id ? [investment._id] : []);

  return (
    <>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        <LockOutlined style={{ marginRight: 8 }} />
        Written by the system — the create, the funding rails, Alto and the note-cycle scheduler.
        Nothing here is editable and nothing here travels with the form's Save.
      </Text>

      {/* Only what the operator reads: the product is the header's, and earnings, % funded and
          Equity synced are columns no flow writes any more. The fund has no listing to look up and
          nothing the ledger can lock. */}
      <Descriptions size="small" bordered column={{ xs: 1, md: 2, xl: 3 }}>
        {!isCreditFund && (
          <Descriptions.Item label="Note listing #">
            {investment.noteListing ? (
              <span
                title={investment.altoRegistered ? ALTO_LOCKED : LISTING_WHY}
                style={{ whiteSpace: 'nowrap' }}
              >
                <code>{investment.noteListing}</code>
              </span>
            ) : (
              '—'
            )}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Alto offering">
          <NoteAltoStatus investment={investment} refetchNote={refetchNote} />
        </Descriptions.Item>
        {!isCreditFund && (
          <Descriptions.Item label="Ledger activity">
            {investment.hasLedgerActivity ? (
              <Tag color="gold">Money has moved — amounts, yield and term are locked</Tag>
            ) : (
              <Tag>No ledger activity</Tag>
            )}
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Balance (in the note's wallet, live from Sila)">
          <NoteWalletBalanceCell
            entry={balances.byId[investment._id]}
            loading={balances.loading}
            error={balances.error}
          />
        </Descriptions.Item>
        <Descriptions.Item label="Raised (ledger)">{money(investment.fundedAmount)}</Descriptions.Item>
        {!isCreditFund && (
          <Descriptions.Item label="Created">{date(investment.createdAt)}</Descriptions.Item>
        )}
        <Descriptions.Item label="Updated">{date(investment.updatedAt)}</Descriptions.Item>
      </Descriptions>

      {isConstitution && (
        <Descriptions
          title="Note cycle"
          extra={<Tag icon={<LockOutlined />}>Set by the note-cycle scheduler</Tag>}
          size="small"
          bordered
          column={{ xs: 1, md: 2, xl: 3 }}
          style={{ marginTop: 16 }}
        >
          <Descriptions.Item label="Term period">{investment.termPeriod || '—'}</Descriptions.Item>
          <Descriptions.Item label="Start date">{date(investment.startDate)}</Descriptions.Item>
          <Descriptions.Item label="Maturity date">
            {date(investment.maturityDate)}
          </Descriptions.Item>
          <Descriptions.Item label="Net yield">
            {investment.netYield == null ? '—' : `${investment.netYield}%`}
          </Descriptions.Item>
          <Descriptions.Item label="Previous cycle">
            {cycleLink(investment.previousCycleId)}
          </Descriptions.Item>
          <Descriptions.Item label="Next cycle">
            {cycleLink(investment.nextCycleId)}
          </Descriptions.Item>
        </Descriptions>
      )}

      {!isEmpty(yieldHistory) && (
        <Table
          title={() => 'Yield history'}
          size="small"
          style={{ marginTop: 16 }}
          dataSource={yieldHistory}
          columns={yieldHistoryColumns}
          rowKey={(row, idx) => `${row.changedAt}-${idx}`}
          pagination={false}
        />
      )}
    </>
  );
}
